import { isValidElement } from 'react';
import PropTypes from 'prop-types';
import { AlertTriangle, CheckCircle, Info, X, XCircle } from 'lucide-react';
import { TONE_CLASSES } from '../foundation/toneClasses.js';
import Button from './Button.jsx';

/** Banner tone → shared palette key (`danger` is Toast's `error`) + default icon. */
const TONES = {
  info: { Icon: Info, palette: 'info' },
  warning: { Icon: AlertTriangle, palette: 'warning' },
  danger: { Icon: XCircle, palette: 'error' },
  success: { Icon: CheckCircle, palette: 'success' },
};

/**
 * Inline status banner: tone-tinted surface, leading icon, title + body, optional primary
 * action and optional dismiss control. Controlled: `onDismiss` only notifies, the parent
 * decides whether to unmount it. `danger` renders `role="alert"`, other tones `role="status"`.
 *
 * @param {object} props
 * @param {'info'|'warning'|'danger'|'success'} [props.tone='info']
 * @param {import('react').ReactNode} [props.title]
 * @param {import('react').ReactNode} [props.children] — Body text.
 * @param {import('lucide-react').LucideIcon} [props.icon] — Overrides the tone's default icon.
 * @param {{ label: string, onClick: () => void } | import('react').ReactElement} [props.action] — Optional action.
 *   An object renders one secondary button; a React element is rendered as is, for several actions or a different variant.
 * @param {() => void} [props.onDismiss] — Renders the dismiss control when provided.
 * @param {string} [props.dismissLabel='Dismiss'] — `aria-label` of the dismiss control (pass a translated string).
 * @param {string} [props.className]
 */
function AlertBanner({
  tone = 'info',
  title,
  children,
  icon,
  action,
  onDismiss,
  dismissLabel = 'Dismiss',
  className = '',
}) {
  const resolvedTone = TONES[tone] ? tone : 'info';
  const { Icon: DefaultIcon, palette } = TONES[resolvedTone];
  const styles = TONE_CLASSES[palette];
  const Icon = icon ?? DefaultIcon;

  return (
    <div
      role={resolvedTone === 'danger' ? 'alert' : 'status'}
      data-tone={resolvedTone}
      className={[
        'flex items-center gap-3 rounded-lg border p-4',
        styles.container,
        className,
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <Icon aria-hidden="true" size={20} className={['shrink-0', styles.icon].join(' ')} />

      <div className="min-w-0 flex-1">
        {title ? (
          <div className={['mb-0.5 text-sm font-semibold', styles.title].join(' ')}>{title}</div>
        ) : null}
        {children ? (
          <div className={['text-sm', styles.message].join(' ')}>{children}</div>
        ) : null}
      </div>

      {action ? (
        <div className={isValidElement(action) ? 'flex shrink-0 items-center gap-2' : 'shrink-0'}>
          {isValidElement(action) ? (
            action
          ) : (
            <Button variant="secondary" size="sm" onClick={action.onClick}>
              {action.label}
            </Button>
          )}
        </div>
      ) : null}

      {onDismiss ? (
        <Button
          variant="ghost"
          size="sm"
          leftIcon={X}
          aria-label={dismissLabel}
          onClick={onDismiss}
          className="shrink-0"
        />
      ) : null}
    </div>
  );
}

AlertBanner.propTypes = {
  tone: PropTypes.oneOf(['info', 'warning', 'danger', 'success']),
  title: PropTypes.node,
  children: PropTypes.node,
  icon: PropTypes.elementType,
  action: PropTypes.oneOfType([
    PropTypes.shape({ label: PropTypes.string.isRequired, onClick: PropTypes.func }),
    PropTypes.element,
  ]),
  onDismiss: PropTypes.func,
  dismissLabel: PropTypes.string,
  className: PropTypes.string,
};

export default AlertBanner;
